import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, ShieldCheck, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const PLAN_LABELS = {
  starter: "Starter · $199/mo",
  growth: "Growth · $499/mo",
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  // ---- Pre-signup PayPal onboarding (iter-73) --------------------------------
  // If the visitor subscribed on /pricing BEFORE creating an account, we get
  // ?paypal_subscription_id=I-XXX&email=... in the URL (or a value stashed in
  // sessionStorage by PaymentSuccess.jsx). We render a green banner + prefill
  // the email so signup happens in one click after PayPal.
  const paypalSubIdFromUrl = params.get("paypal_subscription_id") || "";
  const initialSubId = paypalSubIdFromUrl || sessionStorage.getItem("pending_paypal_subscription_id") || "";
  const initialEmail = params.get("email") || "";

  const [pendingSub, setPendingSub] = useState(null); // {subscription_id, payer_email, package_id, status}
  const [form, setForm] = useState({
    name: "",
    email: initialEmail,
    password: "",
    center_name: "",
    paypal_subscription_id: initialSubId,
  });
  const [loading, setLoading] = useState(false);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Look up the pending sub to populate the banner + confirm the email.
  useEffect(() => {
    const id = form.paypal_subscription_id;
    if (!id) return;
    api.get(`/paypal/pending-subscription/${encodeURIComponent(id)}`)
      .then((r) => {
        setPendingSub(r.data);
        // Autofill email only when the user hasn't typed one yet.
        setForm((f) => ({
          ...f,
          email: f.email || r.data?.payer_email || "",
        }));
      })
      .catch(() => {
        // Non-fatal — backend will still verify at register time.
      });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setLoading(true);
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        center_name: form.center_name.trim() || null,
        paypal_subscription_id: form.paypal_subscription_id || undefined,
      });
      // Clear the pre-signup marker now that the sub is attached.
      sessionStorage.removeItem("pending_paypal_subscription_id");
      sessionStorage.removeItem("pending_paypal_tier");
      toast.success(pendingSub
        ? "Account created — PayPal subscription attached. Welcome to ChildCheckOh!"
        : "Account created! Redirecting to your dashboard…");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const planLabel = pendingSub?.package_id ? (PLAN_LABELS[pendingSub.package_id] || pendingSub.package_id) : null;

  return (
    <div data-testid="register-page" className="min-h-screen grid place-items-center bg-stone-50 p-6">
      <form onSubmit={submit} data-testid="register-form" className="w-full max-w-md rounded-3xl border border-stone-200 bg-white shadow-lift p-8">
        <Link to="/" className="font-display text-xl font-semibold tracking-tight text-stone-900">
          ChildCheck <span className="text-emerald-700">Ohio</span>
        </Link>

        {pendingSub ? (
          <div data-testid="paypal-detected-banner" className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
              <div className="flex-1">
                <div className="font-display text-base font-semibold text-emerald-900">
                  PayPal subscription active
                </div>
                <p className="mt-0.5 text-xs text-emerald-900/80">
                  {planLabel ? <>{planLabel} · </> : null}
                  Complete signup to activate your center. We&apos;ll link this subscription automatically.
                </p>
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-900/70 font-mono">
                  <CreditCard className="w-3 h-3" /> {pendingSub.subscription_id}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <h1 className="mt-5 font-display text-3xl font-medium text-stone-900">
          {pendingSub ? "One last step." : "Start your free trial."}
        </h1>
        <p className="mt-1 text-stone-500 text-sm">
            {pendingSub
              ? "Create your center\u2019s admin login \u2014 you\u2019ll be inside the dashboard in seconds."
              : "No credit card required. 14 days, full access."}
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Your name</Label>
            <Input data-testid="register-name" value={form.name} onChange={(e) => up("name", e.target.value)} required className="mt-2 h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Work email</Label>
            <Input
              data-testid="register-email"
              type="email"
              value={form.email}
              onChange={(e) => up("email", e.target.value)}
              required
              className="mt-2 h-11"
            />
            {pendingSub?.payer_email && pendingSub.payer_email.toLowerCase() !== (form.email || "").toLowerCase() ? (
              <p className="mt-1 text-[11px] text-amber-700" data-testid="paypal-email-mismatch">
                PayPal used <b>{pendingSub.payer_email}</b> — using a different email here is fine, we&apos;ll still attach the subscription.
              </p>
            ) : null}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Password</Label>
            <Input data-testid="register-password" type="password" value={form.password} onChange={(e) => up("password", e.target.value)} required minLength={8} className="mt-2 h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Center name</Label>
            <Input data-testid="register-center" value={form.center_name} onChange={(e) => up("center_name", e.target.value)} placeholder="Columbus Early Learning" className="mt-2 h-11" />
          </div>
        </div>

        <Button type="submit" data-testid="register-submit" disabled={loading} className="mt-6 w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <>
            {pendingSub ? "Activate my center" : "Create my center"} <ArrowRight className="w-4 h-4 ml-1" />
          </>}
        </Button>

        <div className="mt-6 text-sm text-stone-500">
          Already have an account? <Link to="/login" className="text-emerald-700 font-semibold">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
